import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isDate, isEmpty } from 'lodash';
import { IValidationError } from '@shared-libs/interfaces';
import { VALIDATION } from '@shared-libs/constants';
import { ValidationErrorLevel } from '@shared-libs/enums';
import { isAfter, subDays } from 'date-fns';
import { translate } from '@shared-libs/helpers';

/**
 * @static
 */
export class InternalFormValidators {
	/**
	 * This function validates a formcontrol that includes a color hex as value.
	 * It uses the {@link VALIDATION.COLOR_HEX} to test if the value is valid
	 * @param control A bound formcontrol including a color hex as value
	 * @returns {IValidationError} returns null when valid and a ValidationError that can be used by the ValidationErrorComponent when not valid
	 * @see {@link InternalFormValidators.ValidationErrorComponent} ValidationErrorComponent
	 */
	public static colorHex(control: FormControl): IValidationError {
		if (isEmpty(control.value)) return null;
		if (VALIDATION.COLOR_HEX.test(control.value)) {
			return null;
		}
		return {
			colorHex: {
				message: translate('VALIDATION_ERRORS.COLOR_HEX'),
				level: ValidationErrorLevel.error,
			},
		};
	}

	/**
	 * This function validates a formcontrol that includes a number as value.
	 * It should be possible to evenly split 60 by the number
	 * @param control A bound formcontrol including a number as value
	 * @returns {IValidationError} returns null when valid and a ValidationError that can be used by the ValidationErrorComponent when not valid
	 * @see {@link InternalFormValidators.ValidationErrorComponent} ValidationErrorComponent
	 */
	public static reservationInterval(control: FormControl): IValidationError {
		if (isEmpty(control.value)) return null;
		if (60 % control.value === 0) {
			return null;
		}
		return {
			colorHex: {
				message: translate('VALIDATION_ERRORS.RESERVATION_INTERVAL'),
				level: ValidationErrorLevel.error,
			},
		};
	}

	/**
	 * This function validates a formcontrol that includes a social (rijksregisternummer) as value.
	 * It uses custom calculations to test if the value is valid.
	 * @param control A binded formcontrol including a social (rijksregisternummer) as value
	 * @returns {IValidationError} returns null when valid or empty and a ValidationError that can be used by the ValidationErrorComponent when not valid
	 * @see {@link InternalFormValidators.ValidationErrorComponent} ValidationErrorComponent
	 */
	public static social(control: FormControl): IValidationError {
		if (isEmpty(control.value)) return null;
		if (!VALIDATION.SOCIAL_REGEX.test(control.value)) {
			return {
				social: {
					message: translate('VALIDATION_ERRORS.SOCIAL'),
					level: ValidationErrorLevel.error,
				},
			};
		}
		if (control.value.length !== 11 && control.value.length !== 15) {
			return {
				social: {
					message: translate('VALIDATION_ERRORS.SOCIAL'),
					level: ValidationErrorLevel.error,
				},
			};
		}
		const value = control.value.replace(/[^\d]/g, '');
		const valid = value.substr(0, 1) === '0' ? `2${value}` : value;
		const [numberValue, check] = [
			parseInt(valid.substr(0, valid.length - 2)),
			parseInt(valid.substr(valid.length - 2, 2)),
		];
		if (97 - (numberValue % 97) !== check) {
			return {
				social: {
					message: translate('VALIDATION_ERRORS.SOCIAL'),
					level: ValidationErrorLevel.error,
				},
			};
		}
		return null;
	}

	/**
	 * This function checks if the first formControl's date is before the second formControl's date
	 * @param firstControl formControl name that holds the first date
	 * @param secondControl formControl name that holds the second date
	 * @param errorMessagekey key of the validationerror message
	 * @returns {IValidationError} returns null when valid or empty and a ValidationError that can be used by the ValidationErrorComponent when not valid
	 * @see {@link InternalFormValidators.ValidationErrorComponent} ValidationErrorComponent
	 */
	public static dateBeforeDate(firstControl: string, secondControl: string, errorMessagekey: string): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const firstDate = control.get(firstControl)?.value;
			const secondDate = control.get(secondControl)?.value;

			if (
				!(isDate(firstDate) || VALIDATION.INPUT_DATE_STRING.test(firstDate)) ||
				!(isDate(secondDate) || VALIDATION.INPUT_DATE_STRING.test(secondDate))
			) {
				return null;
			}

			if (!!firstDate && !!secondDate && isAfter(firstDate, subDays(secondDate, 1))) {
				return {
					dates: {
						message: translate(`VALIDATION_ERRORS.${errorMessagekey}`, {
							firstDate,
							secondDate,
						}),
						level: ValidationErrorLevel.error,
					},
				};
			}

			return null;
		};
	}
}
